import { useCallback, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { mesAnioNegocio } from '@tanteo/compartido';
import { useApp } from '../../lib/contexto';
import { contarPedidosLocales } from '../../lib/pedidos';
import { ChipPrueba, Insignia } from '../base';
import { usarMenuFlotante } from './usarMenuFlotante';

/**
 * La inicial del nombre y la del primer apellido. Sin apellidos por separado —la identidad del panel
 * o un correo—, la segunda palabra hace de apellido.
 */
function iniciales(nombres: string, apellidos = ''): string {
  const deNombre = nombres.trim().split(/\s+/).filter(Boolean);
  const deApellido = apellidos.trim().split(/\s+/).filter(Boolean);
  const primera = deNombre[0]?.[0];
  if (!primera) return '?';
  const segunda = deApellido[0]?.[0] ?? deNombre[1]?.[0] ?? '';
  return (primera + segunda).toUpperCase();
}

export interface IdentidadPlataforma {
  nombre: string | null;
  rol: 'admin' | 'analista' | null;
}

const CLASE_ITEM =
  'rounded-control min-h-(--alto-control) flex w-full items-center gap-2 px-2 text-left text-contenido font-semibold hover:bg-surface';
const CLASE_TITULO = 'text-muted px-2 py-1 text-etiqueta font-bold uppercase tracking-wide';

function Separador() {
  return <span aria-hidden className="bg-border my-1 block h-px" />;
}

/**
 * Quién soy, en qué negocio estoy y a dónde ir. Un solo componente para dos ubicaciones:
 * desplegable del avatar en pantalla ancha, y dentro del menú único en pantalla angosta.
 */
export function ContenidoCuenta({
  alNavegar,
  identidadPlataforma,
}: {
  /** Cierra el menú que lo contiene cuando el usuario elige un destino. */
  alNavegar?: () => void;
  /** Identidad cuando no hay negocio activo: el equipo de plataforma. */
  identidadPlataforma?: IdentidadPlataforma;
}) {
  const { sesion, negocio, negociosPropios, esSuperadmin, elegirNegocio, salir } = useApp();
  const navegar = useNavigate();
  const ubicacion = useLocation();

  const enPanel = ubicacion.pathname.startsWith('/panel');
  const nombreMostrado = negocio
    ? negocio.nombreVisible
    : identidadPlataforma?.nombre || sesion?.user.email || 'Mi cuenta';
  const rolMostrado = negocio
    ? negocio.rol
    : identidadPlataforma?.rol === 'admin'
      ? 'administrador de plataforma'
      : identidadPlataforma?.rol === 'analista'
        ? 'analista de plataforma'
        : '';
  // Falta lo que el perfil pide y no está: hoy son los nombres y apellidos de la persona.
  const perfilIncompleto = Boolean(negocio) && !(negocio?.nombres && negocio?.apellidos);
  const pedidosAbiertos = negocio ? contarPedidosLocales(negocio.negocioId) : 0;
  const desde = sesion?.user.created_at ? mesAnioNegocio(sesion.user.created_at) : '';

  function ir(negocioId: string | null, ruta: string) {
    elegirNegocio(negocioId);
    navegar(ruta);
    alNavegar?.();
  }

  return (
    <div className="flex flex-col gap-1" data-e2e="cuenta-contenido">
      <div className="space-y-1.5 px-2 pb-2">
        <p className="text-foreground font-bold" data-e2e="cuenta-nombre">
          {nombreMostrado}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {rolMostrado && <Insignia tono="info">{rolMostrado}</Insignia>}
          {negocio?.origen === 'plataforma' && <ChipPrueba />}
        </div>
        {negocio && <p className="text-muted text-etiqueta">{negocio.negocioNombre}</p>}
        {perfilIncompleto && (
          <p className="text-warning text-etiqueta font-semibold" data-e2e="cuenta-perfil-incompleto">
            Perfil incompleto
          </p>
        )}
        {sesion?.user.email && <p className="text-muted truncate text-etiqueta">{sesion.user.email}</p>}
        {desde && <p className="text-muted text-etiqueta">Usuario desde {desde}</p>}
      </div>

      <NavLink role="menuitem" to="/perfil" className={CLASE_ITEM} onClick={alNavegar} data-e2e="cuenta-perfil">
        Editar perfil
      </NavLink>
      <NavLink role="menuitem" to="/preferencias" className={CLASE_ITEM} onClick={alNavegar} data-e2e="cuenta-preferencias">
        Preferencias
      </NavLink>

      {negociosPropios.length > 1 && (
        <>
          <Separador />
          <p className={CLASE_TITULO}>Mis negocios</p>
          {negociosPropios.map((m) => (
            <button
              key={m.membresiaId}
              role="menuitem"
              className={CLASE_ITEM}
              onClick={() => ir(m.negocioId, '/')}
              data-e2e="cuenta-negocio"
            >
              <span className="flex-1 truncate">{m.negocioNombre}</span>
              {m.negocioId === negocio?.negocioId && <span aria-label="negocio activo">✓</span>}
            </button>
          ))}
        </>
      )}

      {/* Los entornos de prueba se listan en el panel, en Negocios: aquí serían una segunda puerta
          a lo mismo. Esta entrada es además la salida de un sandbox, porque limpia el negocio
          activo antes de navegar. */}
      {esSuperadmin === true && !enPanel && (
        <>
          <Separador />
          <button
            role="menuitem"
            className={CLASE_ITEM}
            onClick={() => ir(null, '/panel')}
            data-e2e="cuenta-ir-plataforma"
          >
            Ir al panel
          </button>
        </>
      )}

      <Separador />
      <button
        role="menuitem"
        className={`${CLASE_ITEM} text-destructive`}
        onClick={() => void salir()}
        data-e2e="salir"
      >
        Cerrar sesión
      </button>
      {pedidosAbiertos > 0 && (
        <p className="text-muted px-3 pb-2 text-etiqueta" data-e2e="salir-pedidos">
          {pedidosAbiertos === 1 ? 'Hay 1 pedido abierto' : `Hay ${pedidosAbiertos} pedidos abiertos`} en este equipo: se
          conservan aquí.
        </p>
      )}
    </div>
  );
}

/** El avatar y su desplegable: la forma que toma el bloque de cuenta en pantalla ancha. */
export function MenuCuenta({ identidadPlataforma }: { identidadPlataforma?: IdentidadPlataforma }) {
  const { sesion, negocio } = useApp();
  const [abierto, setAbierto] = useState(false);
  const cerrar = useCallback(() => setAbierto(false), []);
  const { contenedor, disparador } = usarMenuFlotante(abierto, cerrar);

  const nombre = negocio?.nombreVisible || identidadPlataforma?.nombre || sesion?.user.email || '?';
  const letras = iniciales(negocio?.nombres || identidadPlataforma?.nombre || nombre, negocio?.apellidos ?? '');

  return (
    <div ref={contenedor} className="relative">
      <button
        ref={disparador}
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label="Mi cuenta"
        title={nombre}
        data-e2e="menu-cuenta"
        className="bg-primary text-primary-foreground min-h-11 flex h-11 w-11 items-center justify-center rounded-full text-contenido font-extrabold"
      >
        {letras}
      </button>

      {abierto && (
        <div
          role="menu"
          data-e2e="cuenta-abierta"
          className="rounded-card border-border bg-card absolute right-0 z-40 mt-1 max-h-[80vh] w-72 overflow-y-auto border p-2 shadow-lg"
        >
          <ContenidoCuenta alNavegar={cerrar} identidadPlataforma={identidadPlataforma} />
        </div>
      )}
    </div>
  );
}
